import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { toast } from "sonner"
import { activityApi, type LiveEvent } from "./api.ts"
import { getStoredUser } from "./auth-storage.ts"

/**
 * Live updates για ΟΛΗ την εφαρμογή.
 *
 * ΓΙΑΤΙ POLLING ΚΑΙ ΟΧΙ WEBSOCKETS: για να σπρώξει ο server χρειάζεται δεύτερη
 * μόνιμη υπηρεσία δίπλα στο Railway (Reverb ή Pusher), νέα εξάρτηση και
 * πιθανή χρέωση. Δύο-τρεις χρήστες που δουλεύουν ταυτόχρονα δεν το
 * δικαιολογούν. Ένα ερώτημα κάθε δέκα δευτερόλεπτα σε έναν πίνακα με index
 * στο id είναι φθηνότερο από όσο ακούγεται, και αν κάποτε μπουν WebSockets
 * αλλάζει ΜΟΝΟ αυτό το αρχείο — τα υπόλοιπα βλέπουν το ίδιο useLiveRefresh.
 *
 * ΤΡΙΑ ΠΡΑΓΜΑΤΑ ΠΟΥ ΚΡΑΤΑΝΕ ΤΟ ΚΟΣΤΟΣ ΧΑΜΗΛΑ:
 *
 *   1. Παύση όταν το tab είναι κρυμμένο. Ένα ξεχασμένο tab δεν ρωτά τίποτα.
 *   2. Άμεσο ερώτημα μόλις ξαναφανεί, αντί για αναμονή του επόμενου κύκλου —
 *      αλλιώς ο χρήστης γυρίζει σε παλιά οθόνη για δέκα δευτερόλεπτα.
 *   3. Ένα polling για ΟΛΗ την εφαρμογή, στο provider. Αν κάθε λίστα ρωτούσε
 *      μόνη της, πέντε ανοιχτά panels θα σήμαιναν πέντε ερωτήματα.
 */

/**
 * Κάθε πόσο ρωτάει ο browser.
 *
 * Ήταν 10 δευτερόλεπτα και φαινόταν αργό: ο ιδιοκτήτης άλλαζε κάτι στο ένα
 * παράθυρο και μετρούσε μέχρι να το δει στο άλλο. Στα 5 η χειρότερη αναμονή
 * είναι 5 δευτερόλεπτα και η μέση 2,5 — αρκετά γρήγορο ώστε να μοιάζει άμεσο.
 *
 * Το κόστος είναι ένα ερώτημα με index στο id που συνήθως επιστρέφει μηδέν
 * γραμμές· με τρεις χρήστες και δύο tabs ο καθένας, μιλάμε για ένα αίτημα το
 * δευτερόλεπτο. Αν κάποτε χρειαστεί πραγματικά άμεσο, η απάντηση δεν είναι
 * μικρότερος αριθμός εδώ αλλά WebSockets.
 */
const POLL_MS = 5_000

/** Πόσα γεγονότα κρατά ο πίνακας ειδοποιήσεων. Παλιότερα δεν ενδιαφέρουν. */
const KEEP = 40

const SEEN_KEY = "erp_activity_seen_id"

/**
 * Μέχρι ποιο γεγονός έχει καθαρίσει ο χρήστης τη λίστα.
 *
 * Ο καθαρισμός είναι ΤΟΠΙΚΟΣ και όχι διαγραφή στον server: το ημερολόγιο είναι
 * κοινό και αμετάβλητο — δεν πρέπει ο ένας να σβήνει την ιστορία του άλλου.
 * «Καθάρισμα» σημαίνει «δεν θέλω να τα βλέπω άλλο», όχι «δεν έγιναν».
 */
const CLEARED_KEY = "erp_activity_cleared_id"

type LiveContextValue = {
  /** Τα πιο πρόσφατα γεγονότα, νεότερα πρώτα — για τον πίνακα ειδοποιήσεων. */
  events: LiveEvent[]
  /** Πόσα δεν έχει δει ακόμα ο χρήστης. */
  unseen: number
  markAllSeen: () => void
  /** Αδειάζει τη λίστα. Τα επόμενα γεγονότα εμφανίζονται κανονικά. */
  clearAll: () => void
  /** Κρύβει ένα μεμονωμένο γεγονός. */
  dismiss: (id: number) => void
  /** Εγγραφή σε νέα γεγονότα. Επιστρέφει τη συνάρτηση απεγγραφής. */
  subscribe: (fn: (events: LiveEvent[]) => void) => () => void
}

const LiveContext = createContext<LiveContextValue | null>(null)

function readNumber(key: string): number {
  try {
    return Number(localStorage.getItem(key) ?? 0) || 0
  } catch {
    return 0
  }
}

function writeNumber(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // Private mode — ο δείκτης απλώς δεν επιβιώνει το refresh.
  }
}

/** Το γεγονός σε μια πρόταση — ίδια διατύπωση με την αρχική σελίδα. */
export function eventMessage(e: LiveEvent): string {
  // Έτοιμο κείμενο υπερισχύει: τα γεγονότα που δεν αφορούν μία παραγγελία
  // δεν έχουν κωδικό να συνθέσουν.
  if (e.summary) return e.summary

  const code = e.order_code ?? "—"
  const who = e.customer_name ? ` — ${e.customer_name}` : ""

  switch (e.type) {
    case "order.created":
      return `Νέα παραγγελία ${code}${who}`
    case "order.scanned":
      return `Σαρώθηκε η ${code} — έτοιμη${who}`
    case "order.cancelled":
      return `Ακυρώθηκε η ${code}${who}`
    case "order.deleted":
      return `Διαγράφηκε οριστικά η ${code}${who}`
    case "order.status_changed":
      return `Η ${code} άλλαξε σε ${STATUS_LABELS[e.to_status ?? ""] ?? e.to_status}${who}`
    default:
      return `${code}${who}`
  }
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Εκκρεμεί",
  // Νέα κατάσταση, ανάμεσα σε «Εκκρεμεί» και «Σε παραγωγή».
  ready_for_production: "Έτοιμη για παραγωγή",
  in_production: "Σε παραγωγή",
  // Το κλειδί `ready` μένει ίδιο· άλλαξε μόνο η ετικέτα.
  ready: "Έτοιμη για παράδοση",
  delivered: "Παραδόθηκε",
  cancelled: "Ακυρώθηκε",
}

export function LiveUpdatesProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<LiveEvent[]>([])
  const [seenId, setSeenId] = useState<number>(() => readNumber(SEEN_KEY))
  const clearedId = useRef<number>(readNumber(CLEARED_KEY))

  // Ό,τι αλλάζει μέσα στον βρόχο ζει σε ref, όχι σε state: το state θα
  // ξαναέστηνε το setInterval σε κάθε γεγονός.
  const cursor = useRef<number | null>(null)
  const subscribers = useRef(new Set<(events: LiveEvent[]) => void>())
  const inFlight = useRef(false)

  const subscribe = useCallback((fn: (events: LiveEvent[]) => void) => {
    subscribers.current.add(fn)
    return () => {
      subscribers.current.delete(fn)
    }
  }, [])

  const markAllSeen = useCallback(() => {
    const newest = cursor.current ?? 0
    setSeenId(newest)
    writeNumber(SEEN_KEY, newest)
  }, [])

  const clearAll = useCallback(() => {
    const newest = cursor.current ?? 0
    clearedId.current = newest
    writeNumber(CLEARED_KEY, newest)
    // Και «διαβασμένα»: μια λίστα που μόλις άδειασε δεν έχει αδιάβαστα.
    setSeenId(newest)
    writeNumber(SEEN_KEY, newest)
    setEvents([])
  }, [])

  const dismiss = useCallback((id: number) => {
    setEvents((prev) => prev.filter((e) => e.id !== id))
  }, [])

  useEffect(() => {
    let stopped = false

    async function poll() {
      // Χωρίς αυτό, ένα αργό δίκτυο θα στοίβαζε αιτήματα το ένα πάνω στο άλλο.
      if (inFlight.current || document.visibilityState === "hidden") return
      inFlight.current = true

      try {
        const first = cursor.current === null
        const res = await activityApi.since(cursor.current, first ? KEEP : 50)
        if (stopped) return

        cursor.current = res.last_id

        if (first) {
          // Πρώτη φόρτωση: γεμίζει τον πίνακα ΧΩΡΙΣ toasts και χωρίς να
          // ειδοποιήσει τις λίστες — μόλις φορτώθηκαν έτσι κι αλλιώς. Ό,τι
          // είχε καθαρίσει ο χρήστης δεν ξαναγυρίζει μετά το refresh.
          setEvents(res.events.filter((e) => e.id > clearedId.current).slice(0, KEEP))
          return
        }

        if (res.events.length === 0) return

        const fresh = [...res.events].reverse()
        setEvents((prev) => [...fresh, ...prev].slice(0, KEEP))

        // Οι λίστες ξαναφορτώνουν ΓΙΑ ΚΑΘΕ γεγονός, ακόμα και για τα δικά
        // μας: μια αλλαγή από άλλο tab του ίδιου χρήστη είναι εξίσου αλλαγή.
        subscribers.current.forEach((fn) => fn(fresh))

        // Toast ΜΟΝΟ για ξένες ενέργειες. Ειδοποίηση για κάτι που μόλις
        // έκανες μόνος σου είναι θόρυβος — και τη βλέπεις ήδη στην οθόνη σου.
        const me = getStoredUser()
        fresh
          .filter((e) => !me || !e.actor_name || e.actor_name !== me.name)
          .forEach((e) => toast.info(eventMessage(e), { description: e.actor_name ?? undefined }))
      } catch {
        // Σιωπηλά: μια χαμένη δημοσκόπηση δεν είναι γεγονός για τον χρήστη,
        // και ένα toast σφάλματος κάθε δέκα δευτερόλεπτα σε πεσμένο δίκτυο
        // θα ήταν χειρότερο από το ίδιο το πρόβλημα.
      } finally {
        inFlight.current = false
      }
    }

    void poll()
    const timer = setInterval(() => void poll(), POLL_MS)

    // Επιστροφή στο tab: ρώτα ΤΩΡΑ, μη περιμένεις τον επόμενο κύκλο.
    const onVisible = () => {
      if (document.visibilityState === "visible") void poll()
    }
    document.addEventListener("visibilitychange", onVisible)

    return () => {
      stopped = true
      clearInterval(timer)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [])

  const unseen = events.filter((e) => e.id > seenId).length

  return (
    <LiveContext.Provider value={{ events, unseen, markAllSeen, clearAll, dismiss, subscribe }}>
      {children}
    </LiveContext.Provider>
  )
}

export function useLiveUpdates(): LiveContextValue {
  const ctx = useContext(LiveContext)
  if (!ctx) {
    throw new Error("useLiveUpdates χωρίς LiveUpdatesProvider από πάνω")
  }
  return ctx
}

/**
 * «Ξαναφόρτωσε όταν αλλάξει κάτι.»
 *
 * Η συνάρτηση κρατιέται σε ref ώστε η σελίδα να μη χρειάζεται να την τυλίξει
 * σε useCallback: αλλιώς κάθε render θα έκανε απεγγραφή και νέα εγγραφή, και
 * ένα γεγονός που έφτανε ενδιάμεσα θα χανόταν.
 *
 * Παίρνει τα γεγονότα ως όρισμα ώστε μια σελίδα που αφορά ΜΙΑ παραγγελία να
 * μπορεί να αγνοήσει όσα δεν την αφορούν — μια ολική ανανέωση εκεί θα πετούσε
 * τις μισοτελειωμένες αλλαγές του χρήστη.
 *
 * @param types Ποιοι τύποι γεγονότων ενδιαφέρουν. Παράλειψη = όλοι.
 */
export function useLiveRefresh(refetch: (events: LiveEvent[]) => void, types?: string[]): void {
  const { subscribe } = useLiveUpdates()
  const latest = useRef(refetch)
  latest.current = refetch

  const key = types?.join(",") ?? ""

  useEffect(() => {
    const wanted = key ? key.split(",") : null

    return subscribe((events) => {
      const relevant = wanted ? events.filter((e) => wanted.includes(e.type)) : events
      if (relevant.length === 0) return
      latest.current(relevant)
    })
  }, [subscribe, key])
}
